import React, { useState } from 'react';
import { Grid, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import {
  useStripe,
  useElements,
  CardNumberElement,
  CardCvcElement,
  CardExpiryElement,
} from '@stripe/react-stripe-js';
import { useSelector } from 'react-redux';
import {
  GridAction,
  CardInputWrapper,
  BtnSend,
} from '../questionnaire-wizard/payment/payment.style';
import { theme } from '../../theme';
import CustomerService from '../../core/services/customer.service';
import { setupAchPayment } from '../questionnaire-wizard/payment/achPayment';
import AppHelper from '../../core/helpers/AppHelper';

const inputStyle = {
  iconColor: '#c4f0ff',
  color: theme.palette.primary.main,
  fontWeight: '500',
  fontFamily: 'Roboto, Open Sans, Segoe UI, sans-serif',
  fontSize: '22px',
  fontSmoothing: 'antialiased',
  ':-webkit-autofill': {
    color: 'rgba(0, 0, 0, 0.87)',
  },
  '::placeholder': {
    color: 'rgba(0, 0, 0, 0.87)',
  },
};

const customerService = new CustomerService();

const UpdatePaymentMethodWidget = (props) => {
  const { user } = useSelector((state) => state.user);
  const stripe = useStripe();
  const elements = useElements();
  const { setCheckoutError } = props;

  const [isLoading, setIsLoading] = useState(false);
  const [cardError, setCardError] = useState(null);

  const _subscribe = async (data) => {
    try {
      const dataRegistered = {
        payment_method: data.payment_method?.id || data.payment_method,
        payment_type: props.paymentType,
        business_id: props.businessId,
      };
      customerService.setAccessToken(user.token);
      await customerService.changePaymentMethod(dataRegistered);
    } catch (e) {
      setCheckoutError(AppHelper.getError(e));
      await props.reloadClienteSecret();
      throw e;
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isLoading || !stripe || !elements) {
      return;
    }
    setIsLoading(true);
    setCheckoutError(null);
    setCardError(null);

    try {
      let result;
      if (props.paymentType === 'us_bank_account') {
        const setupIntent = await setupAchPayment(stripe, props.clientSecret, {
          name: user.fullName,
          email: user.email,
        });
        result = { setupIntent };
      } else {
        const cardElement = elements.getElement(CardNumberElement);
        result = await stripe.confirmCardSetup(props.clientSecret, {
          payment_method: {
            card: cardElement,
            billing_details: {
              name: user.fullName,
              email: user.email,
            },
          },
        });
      }

      if (result.error) {
        setCheckoutError(result.error.message);
        return;
      }

      await _subscribe(result.setupIntent);
      props.openDialog(false);
      window.location.reload();
    } catch (error) {
      setCheckoutError(AppHelper.getError(error));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCardDetailsChange = (event) => {
    setCardError(event.error ? event.error.message : null);
  };

  return (
    <form onSubmit={handleSubmit}>
      <ToggleButtonGroup
        exclusive
        fullWidth
        disabled={isLoading}
        value={props.paymentType}
        onChange={(event, value) => !isLoading && value && props.setPaymentType(value)}
        sx={{ marginBottom: 3 }}
      >
        <ToggleButton value="card">Card</ToggleButton>
        <ToggleButton value="us_bank_account">Bank account</ToggleButton>
      </ToggleButtonGroup>
      {props.paymentType === 'card' && (
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <Typography>Card number</Typography>
            <CardInputWrapper>
              <CardNumberElement
                options={{ style: { base: inputStyle } }}
                onChange={handleCardDetailsChange}
              />
            </CardInputWrapper>
          </Grid>
          <Grid
            item
            xs={6}
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              flexDirection: 'column',
            }}
          >
            <Typography>Expiration date</Typography>
            <CardInputWrapper>
              <CardExpiryElement
                options={{ style: { base: inputStyle } }}
                onChange={handleCardDetailsChange}
              />
            </CardInputWrapper>
          </Grid>
          <Grid
            item
            xs={6}
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              flexDirection: 'column',
            }}
          >
            <Typography>CVC</Typography>
            <CardInputWrapper>
              <CardCvcElement
                options={{ style: { base: inputStyle } }}
                onChange={handleCardDetailsChange}
              />
            </CardInputWrapper>
          </Grid>
        </Grid>
      )}
      {props.paymentType === 'us_bank_account' && (
        <Grid>
          <Typography marginBottom={2}>
            Securely connect a US bank account with Stripe. Only instant verification is supported.
          </Typography>
          <Typography variant="body2">
            By clicking Agree and update, you authorize FITRADIO to debit the bank account you
            select for amounts owed and future recurring monthly charges under your agreement until
            this authorization is revoked. You can cancel this authorization by contacting FITRADIO.
          </Typography>
        </Grid>
      )}
      {cardError && (
        <Typography color="error" role="alert">{cardError}</Typography>
      )}
      <GridAction>
        <BtnSend type="submit" disabled={isLoading || !stripe}>
          {isLoading
            ? 'Loading'
            : props.paymentType === 'us_bank_account'
              ? 'Agree and update'
              : 'Update'}
        </BtnSend>
      </GridAction>
    </form>
  );
};

export default UpdatePaymentMethodWidget;

UpdatePaymentMethodWidget.propTypes = {
  businessId: PropTypes.number.isRequired,
  clientSecret: PropTypes.string,
  reloadClienteSecret: PropTypes.func,
  openDialog: PropTypes.func,
  setReload: PropTypes.func,
  paymentType: PropTypes.oneOf(['card', 'us_bank_account']).isRequired,
  setPaymentType: PropTypes.func.isRequired,
  setCheckoutError: PropTypes.func.isRequired,
};
